<template>
  <div ref="filtersBar" class="filtersBar">
    <div v-if="showGridType && !$device.isMobile" class="filtersBar__view">
      <span class="filtersBar__viewText">{{ $t('view') }}:</span>
      <button
        class="filtersBar__viewButton"
        :class="{
          'filtersBar__viewButton--active': gridType === 'list',
        }"
        @click="changeGrid('list')"
      >
        <span class="filtersBar__viewIcon icon-grid-list" />
      </button>
      <button
        class="filtersBar__viewButton"
        :class="{
          'filtersBar__viewButton--active': gridType === 'grid',
        }"
        @click="changeGrid('grid')"
      >
        <span class="filtersBar__viewIcon filtersBar__viewIcon--grid icon-grid" />
      </button>
    </div>
    <dropdown-filter
      v-if="sortFilters && sortFilters.length && filterSortValue"
      :title="$t('sorting')"
      :filter="filterSortValue.label"
      class="filtersBar__filter"
      @mouseleave="isShowSortFilter = false"
      @mouseenter="isShowSortFilter = true"
    >
      <dropdown :is-active="isShowSortFilter">
        <form-radio
          :id="`searchSort-${type}`"
          :value="sort"
          :options="sortFilters"
          new-line
          no-wrap
          hide-circle
          @input="onSortChange"
        />
      </dropdown>
    </dropdown-filter>
    <dropdown-filter
      :title="$t('show')"
      :filter="pagination.limit"
      class="filtersBar__filter"
      @mouseleave="isShowListFilter = false"
      @mouseenter="isShowListFilter = true"
    >
      <dropdown :is-active="isShowListFilter">
        <form-radio
          :id="`limitFilter-${type}`"
          :value="pagination.limit"
          :options="limitFilters.values"
          new-line
          no-wrap
          hide-circle
          @input="onLimitChange"
        />
      </dropdown>
    </dropdown-filter>
    <dropdown-filter
      v-if="showPeriod && filterPeriodValue"
      title="Okres"
      :filter="filterPeriodValue.label"
      class="filtersBar__filter"
      @mouseleave="isShowPeriodFilter = false"
      @mouseenter="isShowPeriodFilter = true"
    >
      <dropdown :is-active="isShowPeriodFilter">
        <form-radio
          :id="`periodFilter-${type}`"
          :value="period"
          :options="periodFilters.values"
          new-line
          no-wrap
          hide-circle
          @input="onPeriodChange"
        />
      </dropdown>
    </dropdown-filter>
    <div class="filtersBar__pagination">
      <pagination
        :pagination="pagination"
        :use-href="usePaginationHref"
        default-route="category-slug"
        default-pagination-route="category-page-number"
        :route-name="routeName"
        :route-pagination-name="routePaginationName"
        list-with-newsletter
        @change="changePage"
      />
    </div>
    <div class="filtersBar__mobile">
      <div class="filtersBar__mobileButtons">
        <button
          v-if="showMobileCategories"
          class="filtersBar__mobileButton"
          @click="openMobileCategories = true"
        >
          {{ $t('categories') }}
        </button>
        <button
          v-if="showMobileFilters"
          class="filtersBar__mobileButton"
          @click="openMobileFilters = true"
        >
          {{ $t('filters') }}
        </button>
        <button class="filtersBar__mobileButton" @click="openMobileSort = true">Sortowanie</button>
      </div>
    </div>
    <div class="filtersBar__popup">
      <filters-popup
        v-if="showMobileFilters"
        :title="$t('filters')"
        :is-open="openMobileFilters"
        @close="openMobileFilters = false"
      >
        <template slot="content">
          <search-filters prefix="mobile" />
        </template>
        <template slot="footer">
          <div class="filtersBar__filtersMobileFooter searchPage__filtersMobileFooter">
            <div class="filtersBar__filtersMobileFooterButton">
              <v-button
                :text="$t('clearFilters')"
                theme="lightPurple"
                full-width
                @click="clearFilters"
              />
            </div>
            <div class="filtersBar__filtersMobileFooterButton">
              <v-button
                :text="$t('apply')"
                theme="purple"
                full-width
                @click="openMobileFilters = false"
              />
            </div>
          </div>
        </template>
      </filters-popup>
      <filters-popup
        v-if="showMobileFilters"
        title="Wybierz kategorie"
        :is-open="openMobileCategories"
        disable-content-padding
        @close="openMobileCategories = false"
      >
        <template slot="content">
          <search-categories prefix="mobile" @select="openMobileCategories = false" />
        </template>
      </filters-popup>
      <filters-popup
        class="filtersBar__mobileSort"
        title="Opcje"
        :is-open="openMobileSort"
        @close="openMobileSort = false"
      >
        <template slot="content">
          <form-radio
            :id="`searchSortMobile-${type}`"
            :value="sort"
            :options="sortFilters"
            new-line
            label="Sortowanie"
            class="filtersBar__mobileSortItem"
            @input="onSortChange"
          />
          <form-radio
            :id="`limitFilterMobile-${type}`"
            :value="pagination.limit"
            :options="limitFilters.values"
            new-line
            label="Pokaż"
            class="filtersBar__mobileSortItem"
            @input="onLimitChange"
          />

          <form-radio
            v-if="showPeriod && filterPeriodValue"
            :id="`periodFilterMobile-${type}`"
            :value="period"
            :options="periodFilters.values"
            new-line
            label="Okres"
            class="filtersBar__mobileSortItem"
            @input="onPeriodChange"
          />
        </template>
        <template slot="footer">
          <div class="filtersBar__mobileSortItemFooter">
            <v-button :text="$t('apply')" theme="purple" @click="openMobileSort = false" />
          </div>
        </template>
      </filters-popup>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import Sticky from 'sticky-js';
import SearchFilters from '~/components/Search/SearchFilters';
import DropdownFilter from '~/components/DropdownFilter';
import Dropdown from '~/components/Dropdown';
import FormRadio from '~/components/Form/FormRadio';
import Pagination from '~/components/Pagination';
import FiltersPopup from '~/components/FiltersPopup';
import VButton from '~/components/VButton';
import SearchCategories from '~/components/Search/SearchCategories';

export default {
  name: 'FiltersBar',
  components: {
    SearchCategories,
    FiltersPopup,
    Pagination,
    FormRadio,
    Dropdown,
    DropdownFilter,
    VButton,
    SearchFilters,
  },
  props: {
    type: {
      type: String,
      default: '',
    },
    stickyBreakpoint: {
      type: Number,
      default: 0,
    },
    showGridType: {
      type: Boolean,
      default: false,
    },
    showPeriod: {
      type: Boolean,
      default: false,
    },
    showMobileFilters: {
      type: Boolean,
      default: false,
    },
    showMobileCategories: {
      type: Boolean,
      default: false,
    },
    usePaginationHref: {
      type: Boolean,
      default: false,
    },
    routeName: {
      type: String,
      default: 'search',
    },
    routePaginationName: {
      type: String,
      default: 'search-page-number',
    },
  },
  data() {
    return {
      stickySettings: undefined,
      isShowSortFilter: false,
      isShowListFilter: false,
      isShowPeriodFilter: false,
      openMobileFilters: false,
      openMobileCategories: false,
      openMobileSort: false,
      gridType: this.$cookies.get(`grid-style-${this.type}`)
        ? this.$cookies.get(`grid-style-${this.type}`)
        : 'grid',
    };
  },
  computed: {
    ...mapState({
      sort(state) {
        return state.productsFilters[this.type].sort;
      },
      sortFilters(state) {
        return state.productsFilters[this.type].sortFilters;
      },
      pagination(state) {
        return state.productsFilters[this.type].pagination;
      },
      limitFilters: state => state.productsFilters.limitFilters,
      periodFilters: state => state.productsFilters.periodFilters,
      period(state) {
        return state.productsFilters[this.type].period;
      },
      activeFilters(state) {
        return state.productsFilters[this.type].activeFilters;
      },
      categories(state) {
        return state.productsFilters[this.type].categories;
      },
    }),
    filterPeriodValue() {
      return this.periodFilters.values.find(x => x.value === parseInt(this.period));
    },
    filterSortValue() {
      return this.sortFilters.find(x => x.active === true);
    },
  },
  mounted() {
    this.sticky();
    window.addEventListener('resize', this.sticky);
  },
  destroyed() {
    window.removeEventListener('resize', this.sticky);
  },
  methods: {
    ...mapActions({
      changePagination: 'productsFilters/changePagination',
      changeSort: 'productsFilters/changeSort',
      changePeriod: 'productsFilters/changePeriod',
    }),
    ...mapMutations({
      resetState: 'productsFilters/resetState',
    }),
    changeGrid(type) {
      this.$cookies.set(`grid-style-${this.type}`, type);
      this.gridType = type;
      this.$emit('grid-change', type);
    },
    changePage(pageNum) {
      this.changePagination({
        type: this.type,
        value: {
          ...this.pagination,
          offset: (parseInt(pageNum) - 1) * this.pagination.limit,
        },
      });
    },
    onSortChange(val) {
      this.changeSort({
        type: this.type,
        value: val,
      });
    },
    onLimitChange(val) {
      this.changePagination({
        type: this.type,
        value: { ...this.pagination, limit: val, offset: 0 },
      });
    },
    onPeriodChange(val) {
      this.changePeriod({
        type: this.type,
        value: val,
      });
    },
    clearFilters() {
      this.resetFilters({ type: this.type });
      if (this.type === 'search') {
        this.updateUrlParams(this.localePath({ name: 'search' }), {
          ...this.activeFilters,
          categories: this.categories,
        });
      }
    },
    sticky() {
      if (
        !this.stickySettings &&
        this.stickyBreakpoint &&
        window.innerWidth <= this.stickyBreakpoint
      ) {
        this.stickySettings = new Sticky('.filtersBar', {
          stickyClass: 'filtersBar--sticky',
          wrap: true,
          marginTop: 58,
        });
      }
      if (this.stickySettings && window.innerWidth > this.stickyBreakpoint) {
        if (this.$refs.filtersBar.parentNode.tagName.toLowerCase() === 'span') {
          this.stickySettings.destroy();
          this.stickySettings.elements[0].removeClass('filtersBar--sticky');
          this.stickySettings.elements[0].removeAttribute('style');
          this.unwrap(this.stickySettings.elements[0].parentNode);
          this.stickySettings = undefined;
        }
      }
    },
    unwrap(el) {
      // place childNodes in document fragment
      const docFrag = document.createDocumentFragment();
      while (el.firstChild) {
        const child = el.removeChild(el.firstChild);
        docFrag.appendChild(child);
      }

      // replace sidebar with document fragment
      el.parentNode.replaceChild(docFrag, el);
    },
  },
};
</script>

<style lang="scss">
.filtersBar {
  $root: &;
  display: flex;
  align-items: center;
  background-color: $cWhite;
  font-size: 1.2rem;
  line-height: 1.5rem;
  letter-spacing: 0.05em;
  color: $cAddDark02;

  &__view {
    display: flex;
    align-items: center;
    margin-right: 2.5rem;
  }

  &__viewText {
    text-transform: uppercase;
    padding-right: 1.2rem;
    letter-spacing: 0.05em;

    @include rwd('tablet') {
      padding-right: 0.5rem;
    }
  }

  &__viewButton {
    padding: 5px;
    font-size: 1.2rem;
    line-height: 1.2rem;
    color: $cAddDark02;
    transition: color $tr;

    &--active {
      @include rwd('tablet') {
        display: none;
      }
    }

    &--active,
    &:hover {
      color: $cMain;
    }
  }

  &__viewIcon {
    &--grid {
      font-size: 1.3rem;
      line-height: 1.3rem;
    }
  }

  &__filter {
    margin-right: 2.5rem;

    @include rwd('large-tablet') {
      display: none;
    }
  }

  &__pagination {
    margin-left: auto;

    @include rwd('large-tablet') {
      display: none;
    }
  }

  &__mobile {
    display: none;
    margin-left: auto;

    @include rwd('large-tablet') {
      display: block;
    }

    @include rwd('small-phone') {
      margin-right: -0.3rem;
    }
  }

  &__mobileButton {
    font-size: 1.4rem;
    line-height: 4rem;
    font-weight: 600;
    margin-right: 0.7rem;

    &:last-of-type {
      margin-right: 0;
    }
  }

  &__mobileSortItem {
    margin-bottom: 3rem;
    padding-bottom: 3rem;
    border-bottom: 1px solid $cAddLight;
  }

  &__mobileSortItemFooter {
    display: flex;
    width: 100%;
    justify-content: center;
  }

  &__filtersMobileFooter {
    display: flex;
    margin-left: -5px;
    margin-right: -5px;
    width: 100%;
  }

  &__filtersMobileFooterButton {
    flex: 0 0 50%;
    max-width: 50%;
    padding-left: 5px;
    padding-right: 5px;
  }

  &--sticky {
    z-index: $zHeader - 2;
    box-shadow: 0 5px 15px rgba(23, 25, 51, 0.05);
    width: 100% !important;
    margin-left: 0 !important;
    left: 0 !important;
  }
}
</style>
